import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Eye, EyeOff, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';

const PublicProfileForm = ({ 
  publicProfile, 
  setPublicProfile, 
  profile, 
  stats, 
  text, 
  language, 
  onSubmit, 
  isUpdating 
}) => {
  return (
    <div className="space-y-6">
      {/* Public Profile Form */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-2">
          <Award className="w-6 h-6 text-[#D4AF37]" />
          <h2 className="font-serif text-xl font-semibold text-[#1A252C]">
            {text.publicProfileTitle}
          </h2>
        </div>
        <p className="text-[#546A76] mb-6">{text.publicProfileDesc}</p>
        
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Public Toggle */}
          <div className="flex items-start gap-4 p-4 rounded-xl border border-[#E2E8F0]">
            <Checkbox
              id="is_public"
              checked={publicProfile.is_public}
              onCheckedChange={(checked) => setPublicProfile({...publicProfile, is_public: checked})}
              className="mt-1"
            />
            <div className="flex-1">
              <label htmlFor="is_public" className="font-medium text-[#1A252C] cursor-pointer">
                {text.makePublic}
              </label>
              <p className="text-sm text-[#546A76] flex items-center gap-1 mt-1">
                {publicProfile.is_public ? (
                  <>
                    <Eye className="w-4 h-4 text-green-600" />
                    <span className="text-green-600">{text.publicVisible}</span>
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4" />
                    {text.privateHidden}
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="display_name">{text.displayName}</Label>
            <Input
              id="display_name"
              value={publicProfile.display_name || ''}
              onChange={(e) => setPublicProfile({...publicProfile, display_name: e.target.value})}
              placeholder={profile ? `${profile.first_name} ${profile.last_name}` : ''}
              className="rounded-xl"
            />
            <p className="text-xs text-[#546A76]">{text.displayNameHint}</p>
          </div>

          {/* Public Message */}
          <div className="space-y-2">
            <Label htmlFor="public_message">{text.publicMessage}</Label>
            <Textarea
              id="public_message"
              value={publicProfile.public_message || ''}
              onChange={(e) => setPublicProfile({...publicProfile, public_message: e.target.value})}
              placeholder={language === 'fr' ? 'Je soutiens la Fondation Enat parce que...' : 'I support Fondation Enat because...'}
              className="rounded-xl min-h-[100px]"
              maxLength={280}
            />
            <p className="text-xs text-[#546A76]">{text.publicMessageHint}</p>
          </div>

          {/* Show Amount Toggle */}
          <div className="flex items-start gap-4 p-4 rounded-xl border border-[#E2E8F0]">
            <Checkbox
              id="show_amount"
              checked={publicProfile.show_amount}
              onCheckedChange={(checked) => setPublicProfile({...publicProfile, show_amount: checked})}
              className="mt-1"
            />
            <div className="flex-1">
              <label htmlFor="show_amount" className="font-medium text-[#1A252C] cursor-pointer">
                {text.showAmount}
              </label>
              <p className="text-sm text-[#546A76] mt-1">{text.showAmountHint}</p>
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={isUpdating}
              className="gold-btn rounded-full"
            >
              {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : text.saveChanges}
            </Button>
            
            <Link to="/wall-of-honor">
              <Button type="button" variant="outline" className="rounded-full">
                <ExternalLink className="w-4 h-4 mr-2" />
                {text.viewWallOfHonor}
              </Button>
            </Link>
          </div>
        </form>
      </div>

      {/* Preview Card */}
      {publicProfile.is_public && (
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="text-sm font-medium text-[#546A76] mb-4">
            {language === 'fr' ? 'Aperçu de votre profil public' : 'Preview of your public profile'}
          </h3>
          <div className="bg-[#FAFAFA] rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B5952F] flex items-center justify-center text-white text-xl font-bold">
                {(publicProfile.display_name || profile?.first_name)?.charAt(0)?.toUpperCase() || 'D'}
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-[#1A252C] text-lg">
                  {publicProfile.display_name || `${profile?.first_name} ${profile?.last_name}`}
                </h4>
                {publicProfile.show_amount && stats?.total_donated > 0 && (
                  <p className="text-[#D4AF37] font-medium mt-1">
                    {stats.total_donated.toFixed(0)}$ CAD {language === 'fr' ? 'donné' : 'donated'}
                  </p>
                )}
                {publicProfile.public_message && (
                  <p className="text-sm text-[#546A76] mt-2 italic">
                    "{publicProfile.public_message}"
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicProfileForm;
